<template>
  <div :class="orientation === 'horizontal' ? 'flex justify-between items-center' : ''">
    <p v-if="label" class="label">{{ label }}</p>
    <div class="input-container">
      <input
        :placeholder="props.type === 'number' ? '0' : placeholder"
        @input="handleInput"
        inputmode="numeric"
        :value="value"
        :type="type === 'password' && !visible ? type : 'text'"
        ref="inputRef"
        :disabled="!editable"
      />
      <span class="text-[#E4E4E4] text-xs">{{ unit }}</span>
      <Icon
        v-if="type === 'password'"
        :name="visible ? 'eye_off' : 'eye_on'"
        :size="16"
        color="#E4E4E4"
        @click="visible = !visible"
      />
    </div>
    <p v-if="hint" class="italic pt-1 text-[#95989C] text-xs">{{ hint }}</p>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import Icon from '../Icon.vue'

const emit = defineEmits(['update:value'])
const props = defineProps({
  value: {
    type: [Number, String],
    required: true,
  },
  unit: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'number',
  },
  min: {
    type: Number,
    default: null,
  },
  max: {
    type: Number,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  hint: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  orientation: {
    type: String,
    default: 'vertical',
  },
  editable: {
    type: Boolean,
    default: true,
  },
})

const visible = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target) {
    if (props.type === 'number') {
      const cursorPosition = target.selectionStart || 0
      // Allow negative sign and numeric input
      let numericValue = target.value.replace(/[^0-9.\-]/g, '')

      // Handle case where minus sign is not at the beginning
      if (numericValue.includes('-')) {
        if (numericValue.indexOf('-') === 0) {
          numericValue = '-' + numericValue.substring(1).replace(/-/g, '')
        } else {
          numericValue = numericValue.replace(/-/g, '')
        }
      }

      // Apply maxLength constraint if provided
      if (props.maxLength !== null) {
        // For numeric inputs, we count digits after removing decimal point and minus sign
        const digitsOnly = numericValue.replace(/[.\-]/g, '')
        if (digitsOnly.length > props.maxLength) {
          // Keep the decimal point and minus sign if they exist
          const hasDecimal = numericValue.includes('.')
          const hasMinus = numericValue.startsWith('-')
          const maxDigits = props.maxLength

          if (hasDecimal) {
            const [whole, decimal] = numericValue.split('.')
            const wholeDigits = hasMinus ? whole.slice(1) : whole
            const remainingLength = maxDigits - wholeDigits.length
            numericValue = whole + '.' + (decimal || '').slice(0, remainingLength)
          } else {
            numericValue = numericValue.slice(0, maxDigits + (hasMinus ? 1 : 0))
          }
        }
      }

      // Calculate new cursor position based on removed characters
      const lengthDiff = target.value.length - numericValue.length
      const newCursorPosition = Math.max(0, cursorPosition - lengthDiff)

      // Set the input value directly
      target.value = numericValue

      // Apply min and max constraints if provided
      let numValue = Number(numericValue)
      if (!isNaN(numValue)) {
        if (props.min !== null && numValue < props.min) {
          numValue = props.min
          target.value = String(numValue)
        }
        if (props.max !== null && numValue > props.max) {
          numValue = props.max
          target.value = String(numValue)
        }
      }

      // Restore cursor position
      target.setSelectionRange(newCursorPosition, newCursorPosition)
      emit('update:value', numValue)
    } else {
      // For non-numeric inputs, apply maxLength if provided
      if (props.maxLength !== null && target.value.length > props.maxLength) {
        target.value = target.value.slice(0, props.maxLength)
      }
      emit('update:value', target.value)
    }

    // Prevent default to avoid double updates
    event.preventDefault()
  }
}
</script>

<style scoped>
.input-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--idi-shade2-85);
  border-radius: 6px;
  padding: 8px 10px;
  gap: 4px;
  border: 2px solid transparent;
  overflow: hidden;
}
input {
  background-color: transparent;
  flex: 1;
  border: none;
  outline: none;
  color: #e4e4e4;
}
.input-container:focus-within {
  border: 2px solid var(--idi-shade5-50);
}
</style>
